import React, { useState, useEffect, useRef } from 'react';
import { 
  BookOpen, CheckCircle, XCircle, RefreshCw, ChevronRight, Send, 
  AlertCircle, Loader2, Upload, Code, GraduationCap, Microscope,
  Camera, Eye, Shield, AlertTriangle, Ban, Key, Printer
} from 'lucide-react';

const cssStyles = `
  /* Global Resets & Vars */
  :root {
    --primary: #4f46e5;
    --primary-dark: #4338ca;
    --primary-light: #e0e7ff;
    --bg-page: #f8fafc;
    --bg-card: rgba(255, 255, 255, 0.85);
    --text-main: #0f172a;
    --text-muted: #64748b;
    --border: #e2e8f0;
    --radius-lg: 1.5rem;
    --radius-md: 0.75rem;
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --danger: #dc2626;
    --success: #16a34a;
    --warning: #ca8a04;
  }

  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    background-color: var(--bg-page);
    color: var(--text-main);
  }

  * {
    color: var(--text-main);
  }

  /* Animations */
  @keyframes fadeInSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    50% { opacity: 0.5; }
  }
  @keyframes ping {
    75%, 100% { transform: scale(2); opacity: 0; }
  }
  @keyframes scan {
    0%, 100% { transform: translateY(-100%); opacity: 0; }
    50% { opacity: 1; }
    100% { transform: translateY(100%); opacity: 0; }
  }

  /* Layout Components */
  .app-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .main-header {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 50;
    height: 80px;
    display: flex;
    align-items: center;
  }

  .header-content {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .logo-box {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    padding: 8px;
    border-radius: 12px;
    color: white;
    display: flex;
    box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
  }

  .app-title {
    font-size: 1.25rem;
    font-weight: 700;
    background: linear-gradient(to right, #4f46e5, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0;
  }

  .app-subtitle {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    font-weight: 600;
    display: block;
  }

  .abort-btn {
    background: transparent;
    border: 1px solid transparent;
    color: var(--text-muted);
    font-size: 0.875rem;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .abort-btn:hover {
    background: #fef2f2;
    color: var(--danger);
  }

  .main-content {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 48px 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .main-content > * {
    width: 100%;
    max-width: 1024px;
  }

  /* Cards & Containers */
  .glass-card {
    background: var(--bg-card);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: var(--shadow-lg);
    border-radius: var(--radius-lg);
    padding: 32px;
    animation: fadeInSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    color: var(--text-main);
    width: 100%;
    box-sizing: border-box;
  }

  .section-title-group {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
    justify-content: center;
  }

  .icon-feature {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    padding: 12px;
    border-radius: 12px;
    color: white;
    display: flex;
  }

  .section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-main);
    margin: 0;
    text-align: center;
  }

  /* Form Elements */
  .context-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;
    width: 100%;
  }

  .mode-btn {
    padding: 16px;
    border-radius: 16px;
    border: 1px solid var(--border);
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: #64748b !important;
  }

  .mode-btn.active {
    border-color: var(--primary);
    background: rgba(79, 70, 229, 0.05);
    color: var(--primary);
    transform: scale(1.02);
    box-shadow: var(--shadow-sm);
  }

  .mode-label { font-weight: 600; color: #0f172a !important; }
  .mode-desc { font-size: 0.75rem; opacity: 0.8; color: #64748b !important; }

  .info-box {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 16px;
    font-size: 0.875rem;
  }
  .info-box.warning { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }
  .info-box.info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }

  .text-area-wrapper {
    position: relative;
    margin-bottom: 24px;
  }

  .input-textarea {
    width: 100%;
    height: 200px;
    padding: 16px;
    border-radius: 16px;
    border: 1px solid var(--border);
    background: white;
    font-family: inherit;
    font-size: 1rem;
    resize: none;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
    color: #0f172a !important;
  }
  .input-textarea:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  .upload-btn-wrapper {
    position: absolute;
    bottom: 16px;
    right: 16px;
  }
  .upload-label {
    background: #f1f5f9;
    color: var(--text-main);
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--border);
    transition: background 0.2s;
  }
  .upload-label:hover { background: #e2e8f0; }

  .primary-btn {
    width: 100%;
    background: #0f172a;
    color: white;
    border: none;
    padding: 16px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    transition: all 0.2s;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  .primary-btn:hover:not(:disabled) {
    background: #1e293b;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  .primary-btn:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
  }

  .error-banner {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 16px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    font-size: 0.875rem;
  }

  /* Loading State */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 400px;
    text-align: center;
    animation: fadeInSlideUp 0.5s ease-out;
  }
  .loading-spinner-wrapper {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 24px;
  }
  .ping-circle {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #6366f1;
    opacity: 0.2;
    animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  .spinner-box {
    position: relative;
    background: white;
    padding: 20px;
    border-radius: 50%;
    box-shadow: var(--shadow-lg);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .spin-icon { animation: spin 1s linear infinite; color: var(--primary); }

  /* Quiz Interface */
  .quiz-header {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 16px 24px;
    border-radius: 16px;
    border: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    position: sticky;
    top: 100px;
    z-index: 40;
    color: var(--text-main);
    width: 100%;
    box-sizing: border-box;
  }

  .live-badge {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    padding: 6px 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .pulse-dot {
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .question-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    margin-bottom: 24px;
    box-shadow: var(--shadow-sm);
    border: 1px solid var(--border);
    transition: all 0.2s;
    color: var(--text-main);
    width: 100%;
    box-sizing: border-box;
  }
  .question-card:hover {
    box-shadow: var(--shadow-lg);
  }

  .q-layout { display: flex; gap: 20px; }
  
  .q-number {
    width: 32px;
    height: 32px;
    background: #e0e7ff;
    color: var(--primary);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .q-content { flex: 1; }

  .q-type-tag {
    background: #f1f5f9;
    color: var(--text-muted);
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
    margin-bottom: 12px;
  }

  .q-text {
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--text-main);
    margin: 0 0 24px 0;
    line-height: 1.6;
  }

  .tf-options { display: flex; gap: 16px; }
  .tf-label {
    flex: 1;
    cursor: pointer;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
    color: #0f172a !important;
  }
  .tf-label:hover { background: #f8fafc; }
  .tf-label.selected {
    background: var(--primary);
    border-color: var(--primary);
    color: white !important;
    box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
  }

  .text-input {
    width: 100%;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: #f8fafc;
    font-size: 1rem;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    color: #0f172a !important;
  }
  .text-input:focus {
    background: white;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  .submit-btn {
    background: var(--primary);
    color: white;
    padding: 16px 48px;
    border-radius: 16px;
    border: none;
    font-size: 1.125rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.3);
    transition: all 0.2s;
    margin-left: auto;
  }
  .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .submit-btn:hover:not(:disabled) { background: var(--primary-dark); transform: translateY(-2px); }

  /* Results */
  .results-header {
    background: #0f172a;
    color: white;
    padding: 48px;
    border-radius: 24px;
    text-align: center;
    margin-bottom: 32px;
    position: relative;
    overflow: hidden;
    box-shadow: var(--shadow-lg);
    width: 100%;
    box-sizing: border-box;
  }
  .results-bg-decoration {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, rgba(79,70,229,0.2), rgba(168,85,247,0.2));
  }
  .score-display {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 12px;
    margin: 24px 0;
  }
  .score-val { font-size: 5rem; font-weight: 800; line-height: 1; }
  .score-total { font-size: 2rem; opacity: 0.6; }
  
  .result-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
    position: relative;
    z-index: 10;
  }
  .action-btn {
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    transition: all 0.2s;
  }
  .btn-light { background: white; color: #0f172a; }
  .btn-dark { background: rgba(255, 255, 255, 0.1); color: white; backdrop-filter: blur(5px); }
  .btn-light:hover { background: #f1f5f9; }
  .btn-dark:hover { background: rgba(255, 255, 255, 0.2); }

  .review-card {
    padding: 32px;
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    margin-bottom: 24px;
    color: var(--text-main);
    width: 100%;
    box-sizing: border-box;
  }
  .review-card.correct { border-left: 4px solid var(--success); }
  .review-card.incorrect { border-left: 4px solid var(--danger); }

  .review-header { display: flex; justify-content: space-between; margin-bottom: 16px; }
  .status-badge {
    padding: 4px 12px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .status-badge.correct { background: #dcfce7; color: #15803d; }
  .status-badge.incorrect { background: #fee2e2; color: #b91c1c; }

  .answer-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 24px;
  }
  .answer-box { padding: 16px; border-radius: 12px; border: 1px solid var(--border); }
  .answer-box.user.correct { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
  .answer-box.user.incorrect { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
  .answer-box.key { background: #f8fafc; color: var(--text-main); }
  .label-sm { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; color: var(--text-muted); }

  /* HUD & Overlays */
  .proctor-hud {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
    pointer-events: none;
  }

  .warning-toast {
    background: rgba(220, 38, 38, 0.9);
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    backdrop-filter: blur(4px);
    box-shadow: var(--shadow-lg);
    animation: bounce 0.5s;
  }

  .cam-feed {
    width: 200px;
    height: 150px;
    background: black;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: var(--shadow-lg);
  }
  .video-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
  }
  .rec-indicator {
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(0, 0, 0, 0.6);
    padding: 4px 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
  }
  .scan-line {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent, rgba(74, 222, 128, 0.1), transparent);
    animation: scan 3s ease-in-out infinite;
  }

  /* Terminated Screen */
  .terminated-container {
    max-width: 600px;
    margin: 80px auto;
    text-align: center;
    animation: fadeInSlideUp 0.4s;
    color: var(--text-main);
    width: 90%;
  }
  .ban-icon-wrapper {
    width: 100px;
    height: 100px;
    background: #fee2e2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 32px;
    color: #dc2626;
    position: relative;
  }
  .ban-pulse {
    position: absolute;
    inset: 0;
    background: #ef4444;
    border-radius: 50%;
    opacity: 0.2;
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  .violation-box {
    background: white;
    border: 1px solid #fecaca;
    padding: 24px;
    border-radius: 16px;
    text-align: left;
    margin-bottom: 32px;
    box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.1);
  }

  /* Print Styles */
  .print-view { display: none; }
  @media print {
    .app-container > *:not(.print-view) { display: none; }
    .print-view {
      display: block;
      position: fixed;
      inset: 0;
      background: white;
      z-index: 9999;
      padding: 40px;
      color: black;
      font-family: Georgia, serif;
    }
    .print-header { border-bottom: 2px solid black; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
    .print-title { font-size: 24pt; font-weight: bold; margin: 0; }
    .print-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; background: #f8fafc; padding: 20px; border: 1px solid #e2e8f0; }
    .print-table { width: 100%; border-collapse: collapse; }
    .print-table th { text-align: left; padding: 10px; border-bottom: 2px solid #ccc; }
    .print-table td { padding: 12px 10px; border-bottom: 1px solid #eee; vertical-align: top; }
    .print-badge { font-weight: bold; font-size: 0.8rem; padding: 2px 6px; border: 1px solid #ccc; }
  }

  @media (max-width: 768px) {
    .context-grid { grid-template-columns: 1fr; }
    .answer-grid { grid-template-columns: 1fr; }
    .tf-options { flex-direction: column; }
    .q-layout { flex-direction: column; }
    .header-content { padding: 0 16px; }
    .main-content { padding: 24px 16px; }
    .glass-card { padding: 24px 16px; }
    .question-card { padding: 24px 16px; }
    .review-card { padding: 24px 16px; }
    .results-header { padding: 32px 16px; }
    .score-val { font-size: 3.5rem; }
    .section-title { font-size: 1.25rem; }
    .q-text { font-size: 1rem; }
    .mode-btn { padding: 12px; font-size: 0.9rem; }
  }

  @media (max-width: 480px) {
    .main-content { padding: 16px 12px; }
    .glass-card { padding: 16px 12px; }
    .question-card { padding: 16px 12px; }
    .review-card { padding: 16px 12px; }
    .results-header { padding: 24px 12px; }
    .score-val { font-size: 2.5rem; }
    .app-title { font-size: 1rem; }
    .section-title { font-size: 1.1rem; }
    .score-display { flex-direction: row; gap: 4px; }
  }
`;

const VivaSystem = () => {
  // Core State
  const [inputText, setInputText] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState('input');
  const [userAnswers, setUserAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [error, setError] = useState('');
  const [endTime, setEndTime] = useState(null);

  // Mode & Config State
  const [contextMode, setContextMode] = useState('classroom');
  
  // Proctoring State
  const [stream, setStream] = useState(null);
  const [warnings, setWarnings] = useState(0);
  const videoRef = useRef(null);

  // --------------------------------------------------------------------------
  // API Key Management & Load Balancing
  // --------------------------------------------------------------------------

  // IMPORTANT: The execution environment provides the primary key at runtime.
  const apiKey = "AIzaSyCiS1dZKw9EiuO3ESwAY5gWnLFR3P22fxI";
  
  const ADDITIONAL_KEYS = [
    "AIzaSyCGIB6FQV-tidtROPDl-jp7tOKmbdKd5h0", "", "", "", "", "", "", "", ""
  ];
  const activeKeyIndex = useRef(0);

  const getActiveKey = () => {
    const allKeys = [apiKey, ...ADDITIONAL_KEYS].filter(k => k && k.trim().length > 0);
    if (allKeys.length === 0) return apiKey;
    return allKeys[activeKeyIndex.current % allKeys.length];
  };

  const rotateKey = () => {
    activeKeyIndex.current += 1;
    console.warn(`⚠️ Rate Limit Hit. Switching to API Key Index: ${activeKeyIndex.current}`);
  };

  // API Logic
  const generateQuestions = async () => {
    if (!inputText.trim()) {
      setError('Please enter some text, upload a file, or type a topic first.');
      return;
    }
    try {
      await startProctoring();
      setCurrentStep('loading');
      setError('');
      let persona = "You are a strict teacher.";
      if (contextMode === 'research') persona = "Academic panel member reviewing research.";
      else if (contextMode === 'code') persona = "Technical Interviewer.";
      const systemPrompt = `${persona} Generate exactly 10 questions (mix: fill_blank, short_answer, true_false) based on user input. Output strictly raw JSON array: [{"id": 1, "type": "...", "question": "...", "correctAnswer": "..."}]. No markdown.`;
      const userPrompt = `Context: ${contextMode}. Content: ${inputText.substring(0, 15000)}`;
      const result = await callGeminiWithRetry(systemPrompt, userPrompt);
      const jsonStart = result.indexOf('[');
      const jsonEnd = result.lastIndexOf(']') + 1;
      if (jsonStart === -1 || jsonEnd === 0) throw new Error('Invalid AI response structure.');
      const cleanJson = result.substring(jsonStart, jsonEnd);
      const parsedQuestions = JSON.parse(cleanJson);
      if (Array.isArray(parsedQuestions) && parsedQuestions.length > 0) {
        setQuestions(parsedQuestions);
        setCurrentStep('quiz');
      } else {
        throw new Error('No questions generated.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message === 'Permission denied' ? 'Camera access is required.' : 'Failed to generate questions. Try a different topic.');
      stopProctoring();
      setCurrentStep('input');
    }
  };

  const callGeminiWithRetry = async (systemPrompt, userPrompt) => {
    let attempts = 0;
    const maxRetries = 10;
    while (attempts < maxRetries) {
      try {
        const currentKey = getActiveKey();
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent?key=${currentKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: userPrompt }] }],
              systemInstruction: { parts: [{ text: systemPrompt }] },
              generationConfig: { responseMimeType: "application/json", maxOutputTokens: 2500 }
            }),
          }
        );
        if (response.status === 429) { rotateKey(); attempts++; continue; }
        if (!response.ok) throw new Error(`API Error: ${response.status}`);
        const data = await response.json();
        return data.candidates[0].content.parts[0].text;
      } catch (err) {
        attempts++;
        if (attempts >= maxRetries) throw err;
        const delay = 500 * Math.pow(2, Math.min(attempts, 3));
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Proctoring Logic
  const startProctoring = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
    } catch (err) { throw new Error('Permission denied'); }
  };
  const stopProctoring = () => {
    if (stream) { stream.getTracks().forEach(track => track.stop()); setStream(null); }
  };
  const handleViolation = (type) => {
    if (currentStep !== 'quiz') return;
    setWarnings(prev => {
      const newCount = prev + 1;
      if (newCount >= 3) terminateSession();
      return newCount;
    });
    if (navigator.vibrate) navigator.vibrate(200);
  };
  const terminateSession = () => { setCurrentStep('terminated'); stopProctoring(); };
  useEffect(() => { if (videoRef.current && stream) videoRef.current.srcObject = stream; }, [stream, currentStep]);
  useEffect(() => {
    if (currentStep !== 'quiz') return;
    const preventDefault = (e) => { e.preventDefault(); handleViolation('clipboard'); return false; };
    const handleVisibilityChange = () => { if (document.hidden) handleViolation('tab-switch'); };
    const handleBlur = () => handleViolation('focus-lost');
    document.addEventListener('contextmenu', preventDefault);
    document.addEventListener('copy', preventDefault);
    document.addEventListener('paste', preventDefault);
    document.addEventListener('cut', preventDefault);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('contextmenu', preventDefault);
      document.removeEventListener('copy', preventDefault);
      document.removeEventListener('paste', preventDefault);
      document.removeEventListener('cut', preventDefault);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [currentStep]);

  // Quiz Logic
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedExtensions = ['txt', 'tex', 'md', 'js', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) { setError(`File type .${ext} not supported.`); return; }
    const reader = new FileReader();
    reader.onload = (event) => { setInputText(prev => (prev ? prev + '\n\n' : '') + event.target.result); setError(''); };
    reader.readAsText(file);
  };
  const handleAnswerChange = (questionId, value) => { setUserAnswers(prev => ({ ...prev, [questionId]: value })); };
  const calculateResults = () => {
    let newScore = 0;
    questions.forEach(q => {
      const userAnswer = (userAnswers[q.id] || '').trim().toLowerCase();
      const correct = q.correctAnswer.trim().toLowerCase();
      if (userAnswer === correct) newScore++;
      else if (q.type !== 'true_false' && userAnswer.length > 1 && correct.includes(userAnswer)) newScore++;
    });
    setScore(newScore); setEndTime(new Date()); setCurrentStep('results'); stopProctoring();
  };
  const resetSystem = () => {
    setInputText(''); setQuestions([]); setUserAnswers({}); setScore(0);
    setWarnings(0); setEndTime(null); setCurrentStep('input'); setError(''); stopProctoring();
  };

  // Components
  const PrintableReport = () => (
    <div className="print-view">
      <div className="print-header">
        <div>
          <h1 className="print-title">Viva Assessment Report</h1>
          <p style={{ color: '#666' }}>Generated by VivaBot AI</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Date Generated</div>
          <div style={{ fontWeight: 'bold' }}>{endTime?.toLocaleString()}</div>
        </div>
      </div>
      <div className="print-summary">
        <div>
          <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' }}>Mode</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'capitalize' }}>{contextMode}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' }}>Score</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{score} / 10</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' }}>Result</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: score >= 7 ? '#15803d' : '#b45309' }}>
            {score >= 7 ? 'PASS' : 'REVIEW'}
          </div>
        </div>
      </div>
      <table className="print-table">
        <thead>
          <tr>
            <th style={{ width: '40px' }}>#</th>
            <th>Question & Correct Answer</th>
            <th style={{ width: '25%' }}>Your Response</th>
            <th style={{ width: '80px' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {questions.map((q, i) => {
             const userAnswer = (userAnswers[q.id] || '').trim().toLowerCase();
             const correct = q.correctAnswer.trim().toLowerCase();
             const isCorrect = q.type === 'true_false' 
                ? userAnswer === correct 
                : (userAnswer === correct || (userAnswer.length > 1 && correct.includes(userAnswer)));
             return (
               <tr key={q.id}>
                 <td>{i + 1}</td>
                 <td>
                   <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{q.question}</div>
                   <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>Correct: {q.correctAnswer}</div>
                 </td>
                 <td style={{ fontFamily: 'monospace', background: '#f8fafc' }}>{userAnswers[q.id] || '-'}</td>
                 <td>{isCorrect ? <span className="print-badge" style={{color: 'green', borderColor: 'green'}}>CORRECT</span> : <span className="print-badge" style={{color: 'red', borderColor: 'red'}}>WRONG</span>}</td>
               </tr>
             )
          })}
        </tbody>
      </table>
      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #ccc', textAlign: 'center', fontSize: '0.8rem', color: '#999' }}>
        VivaBot • Secure AI Assessment System
      </div>
    </div>
  );

  const ProctorHUD = () => (
    <div className="proctor-hud">
      {warnings > 0 && (
        <div className="warning-toast">
          <AlertTriangle size={20} />
          <span>Strike {warnings}/3</span>
        </div>
      )}
      <div className="cam-feed">
        <video ref={videoRef} autoPlay muted playsInline className="video-el" />
        <div className="rec-indicator">
          <div className="pulse-dot"></div>
          <span>LIVE</span>
        </div>
        <div className="scan-line"></div>
      </div>
    </div>
  );

  return (
    <>
      <style>{cssStyles}</style>
      <div className="app-container">
        <PrintableReport />
        <header className="main-header">
          <div className="header-content">
            <div className="brand-section">
              <div className="logo-box"><BookOpen size={20} /></div>
              <div>
                <h1 className="app-title">VivaBot</h1>
                <span className="app-subtitle">Secure Assessment</span>
              </div>
            </div>
            {currentStep !== 'input' && currentStep !== 'terminated' && (
              <button onClick={resetSystem} className="abort-btn">Abort Session</button>
            )}
          </div>
        </header>

        <main className="main-content">
          {currentStep === 'input' && (
            <div className="glass-card">
              <div className="section-title-group">
                <div className="icon-feature"><BookOpen size={24} /></div>
                <div>
                  <h2 className="section-title">Start Proctored Viva</h2>
                  <p style={{ color: 'var(--text-muted)' }}>Secure AI Assessment Environment</p>
                </div>
              </div>

              <div className="context-grid">
                {[
                  { id: 'research', label: 'Research', icon: Microscope, desc: 'Thesis & Papers' },
                  { id: 'classroom', label: 'Classroom', icon: GraduationCap, desc: 'Standard Viva' },
                  { id: 'code', label: 'Code', icon: Code, desc: 'Tech Interview' },
                ].map(mode => (
                  <button key={mode.id} onClick={() => setContextMode(mode.id)} className={`mode-btn ${contextMode === mode.id ? 'active' : ''}`}>
                    <mode.icon size={24} />
                    <span className="mode-label">{mode.label}</span>
                    <span className="mode-desc">{mode.desc}</span>
                  </button>
                ))}
              </div>

              <div className="info-box warning">
                <Shield size={20} />
                <div>
                  <strong>Proctoring Active:</strong> Camera required. Tab switching & clipboard monitored.
                </div>
              </div>

              <div className="text-area-wrapper">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={`Paste your ${contextMode} material here...`}
                  className="input-textarea"
                />
                <div className="upload-btn-wrapper">
                  <label className="upload-label">
                    <Upload size={16} /> Upload File
                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} accept=".txt,.tex,.md,.js,.py,.c,.cpp,.java,.html,.css,.json" />
                  </label>
                </div>
              </div>

              {error && (
                <div className="error-banner">
                  <AlertCircle size={20} /> {error}
                </div>
              )}

              <button onClick={generateQuestions} disabled={!inputText.trim()} className="primary-btn">
                <Camera size={20} /> <span>Initialize Secure Session</span>
              </button>
            </div>
          )}

          {currentStep === 'loading' && (
            <div className="loading-container">
              <div className="loading-spinner-wrapper">
                <div className="ping-circle"></div>
                <div className="spinner-box"><Loader2 size={40} className="spin-icon" /></div>
              </div>
              <h3 className="section-title">Analyzing Context</h3>
              <p style={{ color: 'var(--text-muted)', marginTop: '8px' }}>Generating questions tailored to {contextMode} mode...</p>
            </div>
          )}

          {currentStep === 'quiz' && (
            <div style={{ animation: 'fadeInSlideUp 0.6s' }}>
              <div className="quiz-header">
                <div className="live-badge">
                  <div className="pulse-dot"></div> Live Monitoring
                </div>
                <div style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
                  Progress: <span style={{ color: 'var(--primary)' }}>{Object.keys(userAnswers).length}</span> / 10
                </div>
              </div>

              {questions.map((q, index) => (
                <div key={q.id} className="question-card">
                  <div className="q-layout">
                    <div className="q-number">{index + 1}</div>
                    <div className="q-content">
                      <span className="q-type-tag">{q.type.replace('_', ' ')}</span>
                      <h3 className="q-text">{q.question}</h3>
                      {q.type === 'true_false' ? (
                        <div className="tf-options">
                          {['True', 'False'].map((option) => (
                            <label key={option} className={`tf-label ${userAnswers[q.id] === option ? 'selected' : ''}`}>
                              <input
                                type="radio"
                                name={`question-${q.id}`}
                                value={option}
                                checked={userAnswers[q.id] === option}
                                onChange={() => handleAnswerChange(q.id, option)}
                                style={{ display: 'none' }}
                              />
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={userAnswers[q.id] || ''}
                          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                          placeholder="Type your answer here..."
                          onPaste={(e) => { e.preventDefault(); handleViolation('paste'); }}
                          className="text-input"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex' }}>
                <button onClick={calculateResults} disabled={Object.keys(userAnswers).length < 10} className="submit-btn">
                  <span>Submit Assessment</span> <ChevronRight size={20} />
                </button>
              </div>
              <ProctorHUD />
            </div>
          )}

          {currentStep === 'results' && (
            <div style={{ animation: 'fadeInSlideUp 0.6s' }}>
              <div className="results-header">
                <div className="results-bg-decoration"></div>
                <div style={{ position: 'relative', zIndex: 10 }}>
                  <h2 style={{ fontSize: '2rem', margin: 0 }}>Assessment Complete</h2>
                  <div className="score-display">
                    <span className="score-val">{score}</span>
                    <span className="score-total">/ 10</span>
                  </div>
                  <p style={{ opacity: 0.8, maxWidth: '500px', margin: '0 auto' }}>
                    {score === 10 ? 'Exceptional performance. Complete mastery.' : 
                     score >= 7 ? 'Strong performance. Solid grasp of concepts.' : 
                     'Review recommended. Focus on highlighted areas.'}
                  </p>
                  <div className="result-actions">
                    <button onClick={() => window.print()} className="action-btn btn-light">
                      <Printer size={16} /> Download Report
                    </button>
                    <button onClick={resetSystem} className="action-btn btn-dark">
                      <RefreshCw size={16} /> New Session
                    </button>
                  </div>
                </div>
              </div>

              {questions.map((q, index) => {
                const userAnswer = (userAnswers[q.id] || '').trim().toLowerCase();
                const correct = q.correctAnswer.trim().toLowerCase();
                const isCorrect = q.type === 'true_false' 
                  ? userAnswer === correct 
                  : (userAnswer === correct || (userAnswer.length > 1 && correct.includes(userAnswer)));
                
                return (
                  <div key={q.id} className={`review-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="review-header">
                      <span style={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase' }}>Question {index + 1}</span>
                      {isCorrect ? 
                        <span className="status-badge correct"><CheckCircle size={14} /> Correct</span> : 
                        <span className="status-badge incorrect"><XCircle size={14} /> Incorrect</span>
                      }
                    </div>
                    <h3 style={{ fontSize: '1.1rem', margin: '0 0 16px 0' }}>{q.question}</h3>
                    <div className="answer-grid">
                      <div className={`answer-box user ${isCorrect ? 'correct' : 'incorrect'}`}>
                        <div className="label-sm">Your Answer</div>
                        <div>{userAnswers[q.id] || <span style={{fontStyle: 'italic', opacity: 0.6}}>No Answer</span>}</div>
                      </div>
                      {!isCorrect && (
                        <div className="answer-box key">
                          <div className="label-sm">Correct Answer</div>
                          <div>{q.correctAnswer}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {currentStep === 'terminated' && (
            <div className="terminated-container">
              <div className="ban-icon-wrapper">
                <div className="ban-pulse"></div>
                <Ban size={48} style={{ position: 'relative' }} />
              </div>
              <h2 className="section-title" style={{ marginBottom: '8px' }}>Session Terminated</h2>
              <p style={{ color: 'var(--text-muted)' }}>Academic Integrity Protocol Triggered</p>
              
              <div className="violation-box" style={{ marginTop: '32px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <AlertTriangle size={24} color="#dc2626" />
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '1rem' }}>Violation Limit Reached</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.5 }}>
                      Multiple suspicious activities detected (Tab Switching / Focus Loss). 
                      The session has been locked.
                    </p>
                  </div>
                </div>
              </div>
              <button onClick={resetSystem} className="primary-btn">Return to Dashboard</button>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default VivaSystem;